import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './HitLFeedPanel.css';

const TYPE_ICONS = {
    code_change:       '🔧',
    tool_call:         '⚡',
    decision:          '🎯',
    config_change:     '⚙️',
    enrollment:        '👤',
    cms_item:          '📄',
    marketplace_agent: '🏪',
    reflection:        '💭',
    recommendation:    '💡',
    command_execution: '💻',
    no_action_needed:  '✅',
};

// Recommendations that carry no code/config diff — acknowledging them is a
// no-op confirmation, not an approval of a change.  Show different copy.
const NO_CHANGE_TYPES = new Set(['no_action_needed']);

function typeIcon(type) {
    return TYPE_ICONS[type] || '📋';
}

function formatTs(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
        return iso;
    }
}

// ── DetailPanel ────────────────────────────────────────────────────────────

function NoActionDetail({ payload }) {
    const p = payload || {};
    return (
        <div className="aqw-no-action-detail">
            <div className="aqw-no-action-banner">
                <span className="aqw-no-action-icon">✅</span>
                <span className="aqw-no-action-label">No changes will be made</span>
            </div>
            <div className="aqw-no-action-section">
                <div className="aqw-no-action-field-label">Why no action is needed</div>
                <p className="aqw-no-action-reason">{p.reason || p.summary || '(no reason provided)'}</p>
            </div>
            {p.evidence && (
                <div className="aqw-no-action-section">
                    <div className="aqw-no-action-field-label">Evidence</div>
                    <pre className="aqw-no-action-evidence">{p.evidence}</pre>
                </div>
            )}
            {p.already_fixed_by && (
                <div className="aqw-no-action-section">
                    <div className="aqw-no-action-field-label">Fixed by</div>
                    <code className="aqw-no-action-fixedby">{p.already_fixed_by}</code>
                </div>
            )}
            {p.triggering_error && (
                <div className="aqw-no-action-section">
                    <div className="aqw-no-action-field-label">Triggering error</div>
                    <div className="aqw-no-action-error-meta">
                        <span>{p.triggering_error.component}</span>
                        <span className="aqw-sep">·</span>
                        <span>{p.triggering_error.error_type}</span>
                    </div>
                    {p.triggering_error.message && (
                        <p className="aqw-no-action-error-msg">{p.triggering_error.message}</p>
                    )}
                </div>
            )}
            <p className="aqw-no-action-hint">
                <strong>Acknowledge</strong> to confirm no action is needed (no code or config changes will be made).<br />
                <strong>Re-open</strong> to trigger a fresh investigation.
            </p>
        </div>
    );
}

function DetailPanel({ item, onClose, onApprove, onReject, showChat, onChatSend, renderDetail }) {
    const [payloadExpanded, setPayloadExpanded] = useState(false);
    const [chatHistory, setChatHistory] = useState([]);
    const [chatDraft, setChatDraft] = useState('');
    const [chatSending, setChatSending] = useState(false);
    const [approving, setApproving] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [confirmState, setConfirmState] = useState(null);
    const [confirmNote, setConfirmNote] = useState('');

    const isNoChange = item && NO_CHANGE_TYPES.has(item.type);
    const chatEndRef = useRef(null);

    React.useEffect(() => {
        setChatHistory([]);
        setChatDraft('');
        setConfirmState(null);
        setConfirmNote('');
        setPayloadExpanded(false);
    }, [item?.id]);

    React.useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatHistory]);

    if (!item) return null;

    const payloadLines = JSON.stringify(item.payload, null, 2).split('\n');
    const previewLines = payloadExpanded ? payloadLines : payloadLines.slice(0, 8);

    const sendChat = async () => {
        const text = chatDraft.trim();
        if (!text || !onChatSend) return;
        setChatHistory((h) => [...h, { role: 'user', content: text, ts: Date.now() }]);
        setChatDraft('');
        setChatSending(true);
        try {
            const data = await onChatSend(item, text);
            setChatHistory((h) => [...h, {
                role: 'assistant',
                content: data?.response || data?.message || '(no reply)',
                ts: Date.now(),
            }]);
        } catch (e) {
            setChatHistory((h) => [...h, {
                role: 'assistant',
                content: `Error: ${e.message}`,
                ts: Date.now(),
                error: true,
            }]);
        } finally {
            setChatSending(false);
        }
    };

    const handleApproveConfirm = async () => {
        setApproving(true);
        await onApprove(item, confirmNote);
        setApproving(false);
        setConfirmState(null);
        setConfirmNote('');
    };

    const handleRejectConfirm = async () => {
        setRejecting(true);
        await onReject(item, confirmNote);
        setRejecting(false);
        setConfirmState(null);
        setConfirmNote('');
    };

    return (
        <div className="aqw-detail-overlay">
            <div className="aqw-detail-header">
                <button className="aqw-close-btn" onClick={onClose} aria-label="Close detail panel">×</button>
                <div className="aqw-detail-title">
                    <span>{typeIcon(item.type)}</span>
                    <span>{item.title}</span>
                </div>
                <span className="aqw-type-badge">{item.type}</span>
            </div>

            <div className="aqw-detail-body">
                {renderDetail ? renderDetail(item) : (
                    <>
                        <p className="aqw-detail-summary">{item.summary}</p>

                        {isNoChange
                            ? <NoActionDetail payload={item.payload} />
                            : (
                                <div className="aqw-payload-block">
                                    <pre className="aqw-payload-pre">{previewLines.join('\n')}</pre>
                                    {payloadLines.length > 8 && (
                                        <button
                                            className="aqw-show-more-btn"
                                            onClick={() => setPayloadExpanded((v) => !v)}
                                        >
                                            {payloadExpanded ? 'Show less' : `Show ${payloadLines.length - 8} more lines`}
                                        </button>
                                    )}
                                </div>
                            )
                        }
                    </>
                )}

                {showChat && onChatSend && (
                    <div className="aqw-chat-section">
                        <div className="aqw-chat-heading">Discuss with Agent</div>
                        <div className="aqw-chat-history">
                            {chatHistory.length === 0 && (
                                <div className="aqw-chat-empty">Start a conversation with this agent.</div>
                            )}
                            {chatHistory.map((m, i) => (
                                <div
                                    key={i}
                                    className={`aqw-chat-msg ${m.role}${m.error ? ' error' : ''}`}
                                >
                                    <span className="aqw-chat-role">{m.role === 'user' ? 'You' : 'Agent'}</span>
                                    <span className="aqw-chat-content">{m.content}</span>
                                </div>
                            ))}
                            <div ref={chatEndRef} />
                        </div>
                        <div className="aqw-chat-input-row">
                            <input
                                className="aqw-chat-input"
                                value={chatDraft}
                                onChange={(e) => setChatDraft(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) sendChat(); }}
                                placeholder="Ask the agent about this item…"
                                disabled={chatSending}
                            />
                            <button
                                className="aqw-chat-send-btn"
                                onClick={sendChat}
                                disabled={chatSending || !chatDraft.trim()}
                            >
                                {chatSending ? '…' : 'Send'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {!renderDetail && (
                <div className="aqw-detail-footer">
                    {confirmState === 'approve' && (
                        <div className="aqw-confirm-row">
                            {!isNoChange && (
                                <input
                                    className="aqw-confirm-input"
                                    placeholder="Notes (optional)"
                                    value={confirmNote}
                                    onChange={(e) => setConfirmNote(e.target.value)}
                                />
                            )}
                            {isNoChange && (
                                <span className="aqw-confirm-hint">No changes will be made to any code or config.</span>
                            )}
                            <button className="aqw-btn-approve" onClick={handleApproveConfirm} disabled={approving}>
                                {approving
                                    ? (isNoChange ? 'Acknowledging…' : 'Approving…')
                                    : (isNoChange ? 'Confirm Acknowledge' : 'Confirm Approve')}
                            </button>
                            <button className="aqw-btn-cancel" onClick={() => setConfirmState(null)}>Cancel</button>
                        </div>
                    )}
                    {confirmState === 'reject' && (
                        <div className="aqw-confirm-row">
                            <input
                                className="aqw-confirm-input"
                                placeholder={isNoChange ? 'Reason for re-opening (optional)' : 'Reason (optional)'}
                                value={confirmNote}
                                onChange={(e) => setConfirmNote(e.target.value)}
                            />
                            <button className="aqw-btn-reject" onClick={handleRejectConfirm} disabled={rejecting}>
                                {rejecting
                                    ? (isNoChange ? 'Re-opening…' : 'Rejecting…')
                                    : (isNoChange ? 'Confirm Re-open' : 'Confirm Reject')}
                            </button>
                            <button className="aqw-btn-cancel" onClick={() => setConfirmState(null)}>Cancel</button>
                        </div>
                    )}
                    {!confirmState && (
                        <>
                            <button className="aqw-btn-approve" onClick={() => setConfirmState('approve')}>
                                {isNoChange ? '✓ Acknowledge' : '✓ Approve'}
                            </button>
                            <button className="aqw-btn-reject" onClick={() => setConfirmState('reject')}>
                                {isNoChange ? '↺ Re-open' : '✗ Reject'}
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

DetailPanel.propTypes = {
    item:         PropTypes.object,
    onClose:      PropTypes.func.isRequired,
    onApprove:    PropTypes.func.isRequired,
    onReject:     PropTypes.func.isRequired,
    showChat:     PropTypes.bool,
    onChatSend:   PropTypes.func,
    renderDetail: PropTypes.func,
};

// ── ItemRow ────────────────────────────────────────────────────────────────

function ItemRow({ item, selected, onSelect, onApprove, onReject }) {
    const [busy, setBusy] = useState(null);
    const isNoChange = NO_CHANGE_TYPES.has(item.type);

    const handleApprove = async (e) => {
        e.stopPropagation();
        setBusy('approve');
        await onApprove(item, '');
        setBusy(null);
    };

    const handleReject = async (e) => {
        e.stopPropagation();
        setBusy('reject');
        await onReject(item, '');
        setBusy(null);
    };

    return (
        <div
            className={`aqw-row${selected ? ' selected' : ''}${isNoChange ? ' aqw-row-no-action' : ''}`}
            onClick={() => onSelect(item)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(item); }}
        >
            <span className={`aqw-priority-dot ${item.priority || 'medium'}`} />
            <div className="aqw-row-left">
                <div className="aqw-row-title">
                    {typeIcon(item.type)} {item.title}
                    {isNoChange && <span className="aqw-no-change-tag">no-op</span>}
                </div>
                <div className="aqw-row-summary">{item.summary}</div>
            </div>
            <div className="aqw-row-right">
                <div className="aqw-row-meta">{formatTs(item.createdAt)}</div>
                <div
                    className="aqw-row-actions"
                    onClick={(e) => e.stopPropagation()}
                >
                    <button
                        className="aqw-btn-approve"
                        disabled={busy !== null}
                        onClick={handleApprove}
                        title={isNoChange ? 'Acknowledge — no changes will be made' : 'Approve'}
                    >
                        {busy === 'approve' ? '…' : '✓'}
                    </button>
                    <button
                        className="aqw-btn-reject"
                        disabled={busy !== null}
                        onClick={handleReject}
                        title={isNoChange ? 'Re-open — trigger a fresh investigation' : 'Reject'}
                    >
                        {busy === 'reject' ? '…' : isNoChange ? '↺' : '✗'}
                    </button>
                </div>
            </div>
        </div>
    );
}

ItemRow.propTypes = {
    item:      PropTypes.object.isRequired,
    selected:  PropTypes.bool,
    onSelect:  PropTypes.func.isRequired,
    onApprove: PropTypes.func.isRequired,
    onReject:  PropTypes.func.isRequired,
};

// ── HitLFeedPanel ──────────────────────────────────────────────────────────

function HitLFeedPanel({
    items,
    loading,
    error,
    onApprove,
    onReject,
    onRetry,
    onItemClick,
    selectedItem,
    onCloseDetail,
    showChat,
    onChatSend,
    layout,
    emptyMessage,
    renderDetail,
}) {
    const isMasterDetail = layout === 'master-detail';

    const handleSelect = (item) => {
        onItemClick?.(item);
    };

    const itemList = (
        <div className="aqw-list">
            {items.length === 0 && !loading && (
                <div className="aqw-empty">{emptyMessage || 'No pending items to review.'}</div>
            )}
            {items.map((item) => (
                <ItemRow
                    key={item.id}
                    item={item}
                    selected={selectedItem?.id === item.id}
                    onSelect={handleSelect}
                    onApprove={onApprove}
                    onReject={onReject}
                />
            ))}
        </div>
    );

    const detailPanel = (
        <DetailPanel
            item={selectedItem}
            onClose={onCloseDetail}
            onApprove={onApprove}
            onReject={onReject}
            showChat={showChat}
            onChatSend={onChatSend}
            renderDetail={renderDetail}
        />
    );

    return (
        <div className="aqw-root">
            {error && (
                <div className="aqw-error">
                    {error}
                    {onRetry && (
                        <button className="aqw-retry-btn" onClick={onRetry}>
                            Retry
                        </button>
                    )}
                </div>
            )}

            {loading && items.length === 0 && (
                <div className="aqw-loading">Loading…</div>
            )}

            {isMasterDetail ? (
                <div className="aqw-master-detail">
                    <div className="aqw-list-col">{itemList}</div>
                    <div className="aqw-detail-col">
                        {selectedItem
                            // When renderDetail is provided in master-detail mode, render it
                            // directly so the custom panel fully owns its layout (no
                            // redundant DetailPanel header or hidden footer).
                            ? (renderDetail ? renderDetail(selectedItem) : detailPanel)
                            : <div className="aqw-detail-placeholder">
                                <span className="aqw-detail-placeholder-icon">📋</span>
                                <p>Select an item from the queue to review it.</p>
                              </div>
                        }
                    </div>
                </div>
            ) : (
                <div style={{ position: 'relative' }}>
                    {itemList}
                    {selectedItem && detailPanel}
                </div>
            )}
        </div>
    );
}

HitLFeedPanel.propTypes = {
    items:         PropTypes.arrayOf(PropTypes.shape({
        id:        PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        title:     PropTypes.string,
        summary:   PropTypes.string,
        type:      PropTypes.string,
        priority:  PropTypes.string,
        createdAt: PropTypes.string,
        payload:   PropTypes.any,
    })).isRequired,
    loading:       PropTypes.bool,
    error:         PropTypes.string,
    onApprove:     PropTypes.func.isRequired,
    onReject:      PropTypes.func.isRequired,
    onRetry:       PropTypes.func,
    onItemClick:   PropTypes.func,
    selectedItem:  PropTypes.object,
    onCloseDetail: PropTypes.func,
    showChat:      PropTypes.bool,
    onChatSend:    PropTypes.func,
    layout:        PropTypes.oneOf(['feed', 'master-detail']),
    emptyMessage:  PropTypes.string,
    renderDetail:  PropTypes.func,
};

HitLFeedPanel.defaultProps = {
    items:        [],
    loading:      false,
    error:        null,
    showChat:     false,
    layout:       'feed',
    emptyMessage: 'No pending items to review.',
};

export default HitLFeedPanel;
